import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Mail, AlertCircle } from 'lucide-react';
import OrganizationSelector from './OrganizationSelector';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

// ==========================================
// 🔧 CONFIGURATION : INTERRUPTEUR D'INSCRIPTION
// Mettez "true" pour réactiver les inscriptions
const ALLOW_SIGNUP = false; 
// ==========================================

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // On garde la logique d'inscription dans le code, mais elle sera pilotée par l'interrupteur
  const [isSignUp, setIsSignUp] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  
  // On récupère signIn ET signUp (au cas où on réactive)
  const { signIn, signUp } = useAuth();
  
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const checkUserOrganization = async (userId: string) => {
    const userDoc = await getDoc(doc(db, 'users', userId));
    return userDoc.exists() && userDoc.data().organizationId;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let user;
      
      // Si on est en mode "Inscription" ET que c'est autorisé
      if (isSignUp && ALLOW_SIGNUP) {
        user = await signUp(email, password);
        setShowOrgSelector(true);
      } else {
        // Mode Connexion classique
        user = await signIn(email, password);
        const hasOrg = await checkUserOrganization(user.uid);
        
        if (!hasOrg) {
          setShowOrgSelector(true);
        } else {
          navigate(from, { replace: true });
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      setError(error.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (showOrgSelector) {
    return (
      <OrganizationSelector 
        onComplete={() => {
          navigate(from, { replace: true });
        }} 
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="card p-8 shadow-xl border-2 border-blue-100">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl shadow-lg">
                <Lock className="h-8 w-8" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white-900">
              {isSignUp ? 'Créer un compte' : 'Connexion GestPrez'}
            </h1>
            <p className="text-gray-600 mt-2">
              {isSignUp ? 'Créez votre compte pour commencer' : 'Accédez à votre espace de gestion'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-white-700 mb-1">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  required
                  className="input pl-10"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white-700 mb-1">
                Mot de passe
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  required
                  className="input pl-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Traitement...
                </div>
              ) : (
                isSignUp ? "S'inscrire" : "Se connecter"
              )}
            </button>
          </form>
          
          {/* BOUTON DE BASCULE DÉSACTIVABLE */}
          <div className="mt-4 text-center">
            <button
              // Si ALLOW_SIGNUP est faux, le bouton est désactivé
              disabled={!ALLOW_SIGNUP}
              onClick={() => ALLOW_SIGNUP && setIsSignUp(!isSignUp)}
              className={`text-sm font-medium transition-colors ${
                ALLOW_SIGNUP 
                  ? 'text-blue-600 hover:text-blue-700 cursor-pointer' 
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              title={!ALLOW_SIGNUP ? "Les inscriptions sont temporairement fermées" : ""}
            >
              {isSignUp 
                ? 'Déjà un compte ? Se connecter' 
                : (ALLOW_SIGNUP ? 'Créer un compte' : 'Créer un compte (Désactivé)')}
            </button>
            
            {!ALLOW_SIGNUP && !isSignUp && (
              <p className="text-xs text-gray-400 mt-2">
                Les inscriptions sont réservées à l'administrateur.
              </p>
            )}
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default Login;
